import { Navigate, Outlet, Route, Routes } from "react-router";
import { TenantProvider } from "./tenant/TenantProvider";
import { AuthProvider } from "./auth/AuthProvider";
import { OrgRoot, RequireAuth } from "./auth/RequireAuth";
import { Gate, StaffHome } from "./auth/access";
import { AppShell } from "./layout/AppShell";
import { ToastProvider } from "./ui";
import { LanguageProvider } from "./i18n/language";

import { PlatformLogin } from "./pages/auth/PlatformLogin";
import { PlatformTwoFa } from "./pages/auth/PlatformTwoFa";
import { OrgStaffLogin } from "./pages/auth/OrgStaffLogin";
import { KarigarLogin } from "./pages/auth/KarigarLogin";
import { SetPassword } from "./pages/auth/SetPassword";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";

import { OrganizationsPage } from "./pages/super-admin/OrganizationsPage";
import { OrganizationDetailPage } from "./pages/super-admin/OrganizationDetailPage";
import { PlatformAuditPage } from "./pages/super-admin/PlatformAuditPage";
import { SystemHealthPage } from "./pages/super-admin/SystemHealthPage";
import { PlatformBrandingPage } from "./pages/branding/PlatformBrandingPage";

import { OrgAdminOverview } from "./pages/org-admin/OrgAdminOverview";
import { PerformanceTrackerPage } from "./pages/org-admin/PerformanceTrackerPage";
import { DepotsPage } from "./pages/org-admin/DepotsPage";
import { SalesRepsPage } from "./pages/org-admin/SalesRepsPage";
import { CountersPage } from "./pages/org-admin/CountersPage";
import { KarigarsPage } from "./pages/org-admin/KarigarsPage";
import { UserProfilePage } from "./pages/org-admin/UserProfilePage";
import { RewardBatchesPage } from "./pages/org-admin/RewardBatchesPage";
import { DispatchPage } from "./pages/org-admin/DispatchPage";
import { StockPage } from "./pages/org-admin/StockPage";
import { PackingLabelPage } from "./pages/karigar/PackingLabelPage";
import { RewardCodesPage } from "./pages/org-admin/RewardCodesPage";
import { RewardSchemesPage } from "./pages/org-admin/RewardSchemesPage";
import { ProductsPage } from "./pages/org-admin/ProductsPage";
import { SettlementsPage } from "./pages/org-admin/SettlementsPage";
import { ApprovalsPage } from "./pages/org-admin/ApprovalsPage";
import { OrgGeneralSettingsPanel } from "./pages/org-admin/OrgSettingsPage";
import { TwoFactorSettings } from "./pages/settings/TwoFactorSettings";
import { InstallAppPage } from "./pages/settings/InstallAppPage";
import { RolesPermissionsPanel } from "./pages/org-admin/RolesPermissionsPanel";
import { OrgAuditPage } from "./pages/org-admin/OrgAuditPage";
import { BroadcastsPage } from "./pages/org-admin/BroadcastsPage";
import { DocumentsPage } from "./pages/org-admin/DocumentsPage";
import { OrgLinksPage } from "./pages/org-admin/OrgLinksPage";
import { OrgBrandingPage } from "./pages/branding/OrgBrandingPage";


import { CounterDashboard } from "./pages/counter/CounterDashboard";
import { CounterKarigarsPage } from "./pages/counter/CounterKarigarsPage";
import { CounterRedemptionsPage } from "./pages/counter/CounterRedemptionsPage";
import { CounterQrPage } from "./pages/counter/CounterQrPage";

import { KarigarHome } from "./pages/karigar/KarigarHome";
import { KarigarScanPage } from "./pages/karigar/KarigarScanPage";
import { KarigarClaimPage } from "./pages/karigar/KarigarClaimPage";
import { KarigarHistoryPage } from "./pages/karigar/KarigarHistoryPage";
import { KarigarDocumentsPage } from "./pages/karigar/KarigarDocumentsPage";
import { KarigarRedeemPage } from "./pages/karigar/KarigarRedeemPage";
import { KarigarInstallPage } from "./pages/karigar/KarigarInstallPage";
import { KarigarRegistrationPage } from "./pages/public/KarigarRegistrationPage";
import { RewardsLandingPage } from "./pages/public/RewardsLandingPage";
import { RewardsSiteSubpage } from "./pages/public/RewardsSiteSubpage";

import { PlatformSettingsLayout } from "./pages/settings/PlatformSettingsLayout";
import { OrgBrandingRedirect, OrgSettingsLayout } from "./pages/settings/OrgSettingsLayout";
import { DEFAULT_BRANDING_GROUP } from "./branding/groups";
import { WhatsAppPanel } from "./pages/settings/WhatsAppPanel";

import { NotFound } from "./pages/NotFound";

/**
 * Route tree — ARCHITECTURE §4.
 *
 * Two things to notice:
 *
 *  • Everything under `/:orgSlug` sits inside TenantProvider, which fetches
 *    that organization's branding once and themes the whole subtree. No page
 *    below it does per-tenant work.
 *
 *  • `RequireAuth` is convenience only. Each API route enforces the same role
 *    and tenant rules server-side, because hiding a link never protects data.
 */
export function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <ToastProvider>
        <Routes>
          {/* Public product homepage; platform sign-in remains available at /login. */}
          <Route element={<RewardsLandingPage />} path="/" />
          <Route element={<RewardsSiteSubpage page="karigars" />} path="/for-karigars" />
          <Route element={<RewardsSiteSubpage page="companies" />} path="/for-companies" />
          <Route element={<RewardsSiteSubpage page="help" />} path="/help" />
          <Route element={<RewardsSiteSubpage page="journey" />} path="/how-it-works" />
          <Route element={<RewardsSiteSubpage page="platform" />} path="/platform" />
          <Route element={<RewardsSiteSubpage page="film" />} path="/product-film" />
          <Route element={<RewardsSiteSubpage page="privacy" />} path="/privacy" />
          <Route element={<RewardsSiteSubpage page="terms" />} path="/terms" />
          <Route element={<RewardsSiteSubpage page="contact" />} path="/contact" />

          {/* ── Platform (super admin) ────────────────────────── */}
          <Route
            element={
              <TenantProvider>
                <PlatformLogin />
              </TenantProvider>
            }
            path="/login"
          />
          <Route
            element={
              <TenantProvider>
                <PlatformTwoFa />
              </TenantProvider>
            }
            path="/login/twofa"
          />
          <Route
            element={
              <TenantProvider>
                <SetPassword />
              </TenantProvider>
            }
            path="/set-password"
          />
          <Route
            element={
              <TenantProvider>
                <ForgotPassword />
              </TenantProvider>
            }
            path="/forgot-password"
          />
          <Route
            element={
              <TenantProvider>
                <ResetPassword />
              </TenantProvider>
            }
            path="/reset-password"
          />

          <Route
            element={
              <TenantProvider>
                <RequireAuth roles={["super_admin"]} />
              </TenantProvider>
            }
          >
            <Route element={<AppShell />}>
              <Route element={<OrganizationsPage />} path="/super-admin/organizations" />
              <Route element={<OrganizationDetailPage />} path="/super-admin/organizations/:orgId" />
              {/* Settings owns Branding as a tab. The old standalone URL is
                  kept as a redirect — it was in the sidebar for weeks and is
                  the kind of link people bookmark. */}
              <Route element={<PlatformSettingsLayout />} path="/super-admin/settings">
                <Route
                  element={<Navigate replace to={`/super-admin/settings/${DEFAULT_BRANDING_GROUP.slug}`} />}
                  index
                />
                <Route element={<WhatsAppPanel />} path="whatsapp" />
                <Route element={<TwoFactorSettings />} path="security" />
                {/* One dynamic route for all seven branding groups: switching
                    tab changes a param, so React Router keeps the editor
                    mounted and an unsaved draft survives. Seven sibling
                    routes would remount it and silently discard edits. */}
                <Route element={<PlatformBrandingPage />} path=":group" />
              </Route>
              <Route
                element={<Navigate replace to="/super-admin/settings" />}
                path="/super-admin/branding"
              />
              <Route element={<PlatformAuditPage />} path="/super-admin/audit" />
              <Route element={<SystemHealthPage />} path="/super-admin/health" />
              <Route
                element={<Navigate replace to="/super-admin/organizations" />}
                path="/super-admin"
              />
            </Route>
          </Route>

          {/* ── Organization: public and sign-in ──────────────── */}
          <Route
            element={
              <TenantProvider>
                <OrgStaffLogin />
              </TenantProvider>
            }
            path="/:orgSlug/login"
          />
          <Route
            element={
              <TenantProvider>
                <KarigarLogin />
              </TenantProvider>
            }
            path="/:orgSlug/login/karigar"
          />
          <Route
            element={
              <TenantProvider>
                <SetPassword />
              </TenantProvider>
            }
            path="/:orgSlug/set-password"
          />
          <Route
            element={
              <TenantProvider>
                <ForgotPassword />
              </TenantProvider>
            }
            path="/:orgSlug/forgot-password"
          />
          <Route
            element={
              <TenantProvider>
                <ResetPassword />
              </TenantProvider>
            }
            path="/:orgSlug/reset-password"
          />
          {/* The link a karigar gets back on WhatsApp after sending the
              poster's message (D-22). The token is the only key. */}
          <Route
            element={
              <TenantProvider>
                <KarigarRegistrationPage />
              </TenantProvider>
            }
            path="/:orgSlug/register/:token"
          />

          {/* ── Organization: the console ───────────────────────
              Every chain role can reach it; each page is shown to whoever
              may view its module (D-16). Settings stay the org admin's. */}
          <Route
            element={
              <TenantProvider>
                <RequireAuth roles={["org_admin", "sales_rep", "distributor", "sub_distributor", "counter"]} />
              </TenantProvider>
            }
          >
            <Route element={<AppShell />}>
              <Route element={<Gate module="overview"><OrgAdminOverview /></Gate>} path="/:orgSlug/admin" />
              <Route element={<Gate module="performance"><PerformanceTrackerPage /></Gate>} path="/:orgSlug/admin/performance" />
              <Route element={<Gate module="depots"><DepotsPage /></Gate>} path="/:orgSlug/admin/depots" />
              <Route element={<Gate module="team"><SalesRepsPage /></Gate>} path="/:orgSlug/admin/sales-reps" />
              <Route element={<Gate module="team"><SalesRepsPage key="distributor" role="distributor" /></Gate>} path="/:orgSlug/admin/distributors" />
              <Route
                element={<Gate module="team"><SalesRepsPage key="sub_distributor" role="sub_distributor" /></Gate>}
                path="/:orgSlug/admin/sub-distributors"
              />
              <Route element={<Gate module="counters"><CountersPage /></Gate>} path="/:orgSlug/admin/counters" />
              <Route element={<Gate module="karigars"><KarigarsPage /></Gate>} path="/:orgSlug/admin/karigars" />
              <Route element={<Gate module="team"><UserProfilePage kind="team" /></Gate>} path="/:orgSlug/admin/sales-reps/:userId" />
              <Route element={<Gate module="team"><UserProfilePage kind="team" /></Gate>} path="/:orgSlug/admin/distributors/:userId" />
              <Route element={<Gate module="team"><UserProfilePage kind="team" /></Gate>} path="/:orgSlug/admin/sub-distributors/:userId" />
              <Route element={<Gate module="counters"><UserProfilePage kind="counters" /></Gate>} path="/:orgSlug/admin/counters/:userId" />
              <Route element={<Gate module="karigars"><UserProfilePage kind="karigars" /></Gate>} path="/:orgSlug/admin/karigars/:userId" />
              <Route element={<Gate module="products"><ProductsPage /></Gate>} path="/:orgSlug/admin/products" />
              <Route element={<Gate module="rewards"><Outlet /></Gate>} path="/:orgSlug/admin/rewards">
                <Route element={<RewardBatchesPage />} index />
                <Route element={<RewardSchemesPage />} path="schemes" />
                <Route element={<RewardCodesPage />} path=":batchId" />
              </Route>
              <Route element={<Gate module="dispatch"><DispatchPage /></Gate>} path="/:orgSlug/admin/dispatch" />
              <Route element={<Gate module="dispatch"><StockPage /></Gate>} path="/:orgSlug/admin/stock" />
              {/* The same payout screen a counter uses — an org admin can
                  pay a karigar directly. */}
              <Route element={<Gate module="payouts"><CounterRedemptionsPage /></Gate>} path="/:orgSlug/admin/payouts" />
              <Route element={<Gate module="settlements"><SettlementsPage /></Gate>} path="/:orgSlug/admin/settlements" />
              <Route element={<Gate module="approvals"><ApprovalsPage /></Gate>} path="/:orgSlug/admin/approvals" />
              <Route element={<Gate module="links"><OrgLinksPage /></Gate>} path="/:orgSlug/admin/links" />
              {/* No WhatsApp route here: the account is shared platform-wide,
                  so it is managed only by the super admin. */}
              <Route element={<Gate admin><OrgSettingsLayout /></Gate>} path="/:orgSlug/admin/settings">
                <Route element={<OrgGeneralSettingsPanel />} index />
                <Route element={<RolesPermissionsPanel />} path="roles" />
                <Route element={<TwoFactorSettings />} path="security" />
                <Route element={<OrgBrandingPage />} path=":group" />
              </Route>
              <Route
                element={<Gate admin><OrgBrandingRedirect /></Gate>}
                path="/:orgSlug/admin/branding"
              />
              <Route element={<Gate module="audit"><OrgAuditPage /></Gate>} path="/:orgSlug/admin/audit" />
              {/* Reachable by every org role, not gated on a module — installing
                  is not a permission, it is a thing anyone holding a phone
                  might want to do. */}
              <Route element={<InstallAppPage />} path="/:orgSlug/admin/install" />
              <Route element={<Gate module="broadcasts"><BroadcastsPage /></Gate>} path="/:orgSlug/admin/broadcasts" />
              <Route element={<Gate module="documents"><DocumentsPage /></Gate>} path="/:orgSlug/admin/documents" />
            </Route>
          </Route>

          {/* ── Organization: sales rep / distributor ─────────── */}
          <Route
            element={
              <TenantProvider>
                <RequireAuth roles={["sales_rep", "distributor", "sub_distributor"]} />
              </TenantProvider>
            }
          >
            <Route element={<AppShell />}>
              {/* A chain role's landing: their first permitted page. */}
              <Route element={<StaffHome />} path="/:orgSlug/sales" />
            </Route>
          </Route>

          {/* ── Organization: counter ─────────────────────────── */}
          <Route
            element={
              <TenantProvider>
                <RequireAuth roles={["counter"]} />
              </TenantProvider>
            }
          >
            <Route element={<AppShell />}>
              <Route element={<CounterDashboard />} path="/:orgSlug/counter" />
              <Route element={<Gate module="karigars"><CounterKarigarsPage /></Gate>} path="/:orgSlug/counter/karigars" />
              <Route element={<Gate module="karigars"><UserProfilePage kind="karigars" /></Gate>} path="/:orgSlug/counter/karigars/:userId" />
              <Route element={<Gate module="payouts"><CounterRedemptionsPage /></Gate>} path="/:orgSlug/counter/redemptions" />
              <Route element={<CounterQrPage />} path="/:orgSlug/counter/qr" />
              <Route element={<InstallAppPage />} path="/:orgSlug/counter/install" />
            </Route>
          </Route>

          {/* ── Organization: karigar (bilingual) ─────────────── */}
          <Route
            element={
              <TenantProvider>
                <RequireAuth roles={["karigar"]} />
              </TenantProvider>
            }
          >
            <Route element={<KarigarHome />} path="/:orgSlug/karigar" />
            <Route element={<KarigarScanPage />} path="/:orgSlug/karigar/scan" />
            <Route element={<KarigarHistoryPage />} path="/:orgSlug/karigar/history" />
            <Route element={<KarigarDocumentsPage />} path="/:orgSlug/karigar/documents" />
            <Route element={<KarigarRedeemPage />} path="/:orgSlug/karigar/redeem" />
            <Route element={<KarigarInstallPage />} path="/:orgSlug/karigar/install" />
          </Route>

          {/*
            Where a printed sticker lands — deliberately outside `RequireAuth`.
            Most people reaching it have never signed in, and the guard would
            send them to the staff password form and lose the code on the way.
            The page handles the signed-out case itself, remembering the
            sticker so it is claimed straight after sign-in.
          */}
          <Route
            element={
              <TenantProvider>
                <KarigarClaimPage />
              </TenantProvider>
            }
            path="/:orgSlug/c/:code"
          />
          {/* Carton and batch QRs (D-20) move stock; they are not product codes. */}
          <Route element={<PackingLabelPage />} path="/:orgSlug/k/:code" />
          <Route element={<PackingLabelPage />} path="/:orgSlug/b/:code" />

          <Route
            element={
              <TenantProvider>
                <OrgRoot />
              </TenantProvider>
            }
            path="/:orgSlug"
          />
          <Route element={<NotFound />} path="*" />
        </Routes>
      </ToastProvider>
    </AuthProvider>
    </LanguageProvider>
  );
}
